(function () {
    "use strict";
    /**
     * Provides common localisation functions
     */
    angular.module("dashboard")
        .service(
            "LocalisationService", [
                "DashboardConfig",
                function LocalisationService(DashboardConfig) {
                    const self = this;

                    // -----------------------------------------------------------------------------
                    // Attributes
                    // -----------------------------------------------------------------------------

                    self.Country = DashboardConfig.CountryCode;
                    self.Configuration = {};

                    // This format is used for the angular bootstrap datepicker control
                    self.DatePickerFormat = "dd-MM-yyyy";

                    // -----------------------------------------------------------------------------
                    // Functions
                    // -----------------------------------------------------------------------------

                    /**
                     * Initialise the Service
                     * @constructor
                     */
                    self.initialise = function () {
                        self.Configuration = DashboardConfig.LocalisationSettings;
                        // Date format used on datepicker directives
                        self.DatePickerFormat = self.Configuration.DatePickerFormat;
                    };

                    /**
                     * Formats a number into the specified number of decimal places
                     * @param value {Number}
                     * @param decimalPlaces {int}
                     */
                    self.formatNumber = function (value, decimalPlaces) {
                        const decimalChar = self.Configuration.NumberFormat.DecimalPoint;
                        const power = Math.pow(10, decimalPlaces);
                        const rounded = Math.round(value * power) / power;
                        return rounded.toString().replace(".", decimalChar);
                    };

                    /**
                     * Format options when displaying currency values
                     *
                     * @typedef {{
                     *      precision:Number,
                     *      decimalPoint:String,
                     *      prefix:String,
                     *      suffix:String
                     * }} FormatCurrencyOptions
                     */

                    /**
                     * Formats Currency according to Localisation Settings for the Country
                     * @param {number} value
                     * @param {FormatCurrencyOptions} options
                     * @returns {string}
                     * @constructor
                     * @param {object} options - Used to overwrite localisation configuration if needed.
                     *  - options.precision
                     *  - options.decimalPoint
                     *  - options.prefix
                     *  - options.suffix
                     */
                    self.formatCurrency = function (value, options) {
                        // Local Variables
                        const sign = value < 0 ? "-" : "";
                        let abs = 0;
                        let decimal = "";
                        let formattedValue = "";
                        options = (typeof (options) == "undefined") ? {} : options;

                        // Set Options
                        options = _.defaults(options, {
                            precision: self.Configuration.CurrencyFormat.Precision,
                            decimalPoint: self.Configuration.CurrencyFormat.DecimalPoint,
                            prefix: self.Configuration.CurrencyFormat.Prefix,
                            suffix: self.Configuration.CurrencyFormat.Suffix
                                .replace(/\${.*}/, "")
                                .replace("''", "")
                        });

                        // Apply Precision
                        value = parseFloat(value).toFixed(options.precision);

                        // Apply Decimal and Separator Formatting
                        if (options.precision > 0) {
                            const sValue = value.toString();
                            abs = sValue.substr(0, sValue.indexOf("."));
                            decimal = (options.precision > 0) ? sValue.substr(sValue.indexOf(".") + 1) : "";
                            options.decimalPoint = (options.precision > 0) ? options.decimalPoint : "";
                            formattedValue = abs;
                            formattedValue = (formattedValue.length > 0) ? formattedValue : "0";
                        } else {
                            formattedValue = value;
                            options.decimalPoint = "";
                        }

                        // Apply thousands separator
                        formattedValue = self.formatNumber(formattedValue);

                        // Apply Prefix and suffix
                        return [
                            sign,
                            options.prefix,
                            formattedValue,
                            options.decimalPoint,
                            decimal,
                            options.suffix
                        ].join("");
                    };

                    /**
                     * Formats the date
                     * @param value
                     * @returns {*}
                     */
                    self.formatDate = function (value) {
                        if (!value) {
                            return value;
                        }
                        return moment(new Date(value)).format(self.Configuration.LongDateFormat);
                    };

                    /**
                     * Formats the date
                     * @param {date} value
                     * @param {string} [format=DD/MM/YYYY HH:mm]
                     * @returns {string}
                     */
                    self.formatDateTime = function (value, format) {
                        if (!value) {
                            return value;
                        }
                        if (!format) {
                            format = self.Configuration.LongDateFormat;
                        }
                        return moment(new Date(value)).format(format);
                    };

                    /**
                     * Formats the date to a full date time format
                     * @param {date} value
                     * @returns {string} - format=YYYY/MM/DD HH:mm A
                     */
                    self.formatToFullDateTime = function (value) {
                        return self.formatDateTime(value, self.Configuration.DateTimeFormat);
                    };

                    /**
                     * Formats the date to a long full date time format
                     * @param {date} value
                     * @returns {string} - format=DDD MMM YYYY HH:mm A
                     */
                    self.formatToFullDateTimeLong = function (value) {
                        return self.formatDateTime(value, self.Configuration.LongDateTimeFormat);
                    };

                    /**
                     * Formats the date
                     * @param value
                     * @returns {string}
                     */
                    self.formatDateShort = function (value) {
                        return moment(new Date(value)).format(self.Configuration.ShortDateFormat);
                    };

                    /**
                     * Formats the date
                     * @param value
                     * @returns {string}
                     */
                    self.formatDateLong = function (value) {
                        return moment(new Date(value)).format(self.Configuration.LongDateFormat);
                    };

                    /**
                     * Format a number with thousands separator
                     * @param {number} value
                     * @returns {string}
                     */
                    self.formatNumber = function (value) {
                        let separator = self.Configuration.NumberFormat.ThousandsSeparator;
                        separator = separator != null ? separator : "";
                        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
                    };

                    /**
                     * Formats the passed in datetime to a format that gets passed through to the API
                     * @param value {Date}
                     */
                    self.formatDateTimeForAPIRequest = function (value) {
                        return moment(new Date(value)).format(self.Configuration.APIRequestDateTimeFormat);
                    };

                    /**
                     * Combines a date and time object and returns one date object
                     * @param dateValue
                     * @param timeValue
                     * @returns {Date}
                     */
                    self.combineDateTime = function (dateValue, timeValue) {
                        if (!dateValue) {
                            dateValue = new Date();
                        }
                        return moment({
                            y: dateValue.getFullYear(),
                            M: dateValue.getMonth(),
                            d: dateValue.getDate(),
                            h: moment(timeValue, "hh:mm").hour(),
                            m: moment(timeValue, "hh:mm").minute()
                        }).toDate();
                    };

                    self.combineDateTimeAndFormat = function (dateValue, timeValue, format) {
                        const value = self.combineDateTime(dateValue, timeValue);
                        return self.formatDateTime(value, format);
                    };

                    self.initialise();
                }
            ]
        );
})();
